import { useMemo } from 'react';
import { create } from 'zustand';
import type { PassportResponse, PassportStampEvent } from '@tg/shared';
import { endpoints } from '@/api/endpoints';

/**
 * Game Passport of the signed-in player (GET /api/me/passport), kept fresh by `passport:stamp` (PassportListener).
 * Null for guests and until loaded: the lobby shows no «New for you» marks then (never a guess).
 */
interface PassportState {
  data: PassportResponse | null;
  error: boolean;
  load(): Promise<void>;
  /** a new stamp from the socket */
  stamp(e: PassportStampEvent): void;
  clear(): void;
}

let seq = 0;

export const usePassport = create<PassportState>()((set, get) => ({
  data: null,
  error: false,
  async load() {
    const my = ++seq;
    try {
      const data = await endpoints.passport();
      if (my === seq) set({ data, error: false });
    } catch {
      if (my === seq) set({ error: true });
    }
  },
  stamp(e) {
    const d = get().data;
    if (!d) return;
    const stamps = d.stamps.some((s) => s.game === e.game) ? d.stamps : [...d.stamps, { game: e.game, firstAt: e.firstAt }];
    const got = new Map(e.rewards.map((r) => [r.id, r]));
    const rewards = d.rewards.map((r) => (got.has(r.id) && !r.claimedAt ? { ...r, claimedAt: e.firstAt } : r));
    set({ data: { ...d, stamps, total: e.total, rewards } });
  },
  clear() {
    seq++;
    set({ data: null, error: false });
  },
}));

const NONE: ReadonlySet<string> = new Set();

/** games with a stamp (empty while not loaded) */
export function useStamped(): ReadonlySet<string> {
  const data = usePassport((s) => s.data);
  return useMemo(() => (data ? new Set(data.stamps.map((s) => s.game)) : NONE), [data]);
}
